import { Close, Tune } from "@mui/icons-material";
import { RetroButton } from "components/RetroButton";
import { CircleButton } from "components/CircleButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as XSquare } from "assets/icons/xsquare.svg";
import { Box, DrawerProps, MenuItem, Typography, useTheme } from "@mui/material";
import { useApproval, useTradeDrawer } from "hooks/useTradeDrawer";
import { buttonConfig, DarkDrawer, NumpadKey } from "components/TradeDrawer";
import { ReactComponent as ArrowRight } from "assets/icons/arrow-right.svg";
import { SmallNumberView } from "components/SmallValueView/SmallValueView";
import { getChainsByMode, Mode, MODES, TRADING_MODE_CONFIG_MAP } from "tradeConfigs";
import { AmountTooSmallDrawer } from "./AmountTooSmallDrawer";
import { useTradeContext } from "contexts/TradeContext";
import { PreferencesDrawer } from "./PreferencesDrawer";
import { Chain, CHAIN_CONFIG } from "chainConfigs";
import { ConfirmDrawer } from "./ConfirmDrawer";
import { BuySellSwitch } from "./BuySellSwitch";
import { RefetchPreview } from "./priceHint";
import { StyledSelect } from "./styled";
import { styled } from "@mui/material";
import RetroModal from "components/RetroModal";
import useTradeStore from "store/trade-store.ts/useTradeStore";
import useUserStoreV2, { DEFAULT_CACHED_SLIPPAGE } from "store/user-store-v2/useUserStoreV2";
import { Value } from "components/Value";
import { useCheckViewPoint } from "hooks/useCheckViewPoint";

interface CrossChainTradeDrawerV2Props {
  chain: Chain;
  groupId: number;
  isFromTrendingCall?: boolean;
  onProcessing?: () => void;
  onProcessed?: () => void;
  onConfirm?: (message?: string) => void;
  onFail?: (message?: string) => void;
  onClose: () => void;
  mode?: Mode;
}

export const CrossChainTradeDrawerV2 = (props: DrawerProps & CrossChainTradeDrawerV2Props) => {
  const {
    defaultMode,
    handleSetMode,
    mode: tradeMode,
    targetTokenPrice,
    targetTokenConfig,
    selectedNativeChain,
    selectedTargetChain,
    targetBalanceUsd,
    targetBalanceAmount,
    handleSetSelectedNativeChain,
    handleSetSelectedTargetChain,
    handleResetToDefaultTargetChain,
  } = useTradeContext();

  const {
    onClose,
    inputUsd,
    priceHint,
    onConfirm,
    targetAmount,
    nativeAmount,
    onNumpadPress,
    onPercentPress,
    isOpenConfirmDrawer,
    shouldConfirmDisabled,
    handleOpenConfirmDrawer,
    priceImpact,
    noValidRouteMessage,
    isLoadingPriceImpact,
    isRefetchingPriceImpact,
    refetchPriceImpact,
    isBalanceExceeded,
    setInputText,
    bridgeFee,
    inputText,
  } = useTradeDrawer({
    tgGroupId: props.groupId,
    isFromTrendingCall: props.isFromTrendingCall,
    onConfirm: props.onConfirm,
    onProcessing: props.onProcessing,
    onProcessed: props.onProcessed,
    onClose: () => {
      handleResetToDefaultTargetChain();
      setModeHandler(defaultMode);
      isCheckingAmountRef.current = false;
      props.onClose();
    },
    onFail: props.onFail,
    propsMode: props.mode,
  });

  const { isApproving, isApproveSucess, isGetCurrentAllowanceLoading, handleApproveAllowance } =
    useApproval({ targetAmount });

  const { getUserBalance } = useTradeStore();

  const mode = props.mode ?? tradeMode;
  const tradeModeConfig = TRADING_MODE_CONFIG_MAP[mode];

  const isSelectable = tradeModeConfig.supportedNativeChains[selectedNativeChain]!.length > 1;

  // Slippage
  const { preferences = { slippage: DEFAULT_CACHED_SLIPPAGE } } = useUserStoreV2();
  const { slippage = 3 } = preferences;
  const [isOpenSlippageDrawer, setIsOpenSlippageDrawer] = useState(false);

  // price hint
  const [hintMessage, setHintMessage] = useState("");

  // Reminder Drawer
  const [shouldShowAmountTooSmall, setShouldShowAmountTooSmall] = useState(false);

  // selected token
  const [fromToken, setFromToken] = useState<string | null>(null);
  const [toToken, setToToken] = useState<string | null>(null);

  // check amount
  const isCheckingAmountRef = useRef(false);

  // Remindar Modal
  const [modalConfig, setModalConfig] = useState<{
    variant: "success" | "error";
    title: string;
    message: string;
  } | null>(null);

  const theme = useTheme();

  const isLoadingRef = useRef(false);

  // Slippage
  const handleOpenSlippageDrawer = () => {
    setIsOpenSlippageDrawer(true);
  };

  // Start Trading
  const handleConfirm = (slippage?: number) => {
    if (
      tradeModeConfig.minimumEntryUSD !== undefined &&
      +inputUsd < tradeModeConfig.minimumEntryUSD
    ) {
      setShouldShowAmountTooSmall(true);
      return;
    }

    handleOpenConfirmDrawer(true);
  };

  const onChangeSelect = (type: "from" | "to", chain: Chain) => {
    if (type === "to") {
      setToToken(chain);
      [MODES.BUY, MODES.SELL].includes(mode)
        ? handleSetSelectedNativeChain(chain)
        : handleSetSelectedTargetChain(chain);
    } else {
      handleSetSelectedNativeChain(chain);
      setFromToken(chain);
    }
  };

  const isTradingWrappedToken =
    [MODES.BUY, MODES.SELL].includes(mode) &&
    CHAIN_CONFIG[selectedTargetChain].wrappedTokenAddress ===
      targetTokenConfig[selectedTargetChain]?.token;

  const fromTokenList = useMemo(() => {
    if (tradeModeConfig.from.isNative) {
      return tradeModeConfig.supportedNativeChains[selectedNativeChain]!.filter(
        (chain) =>
          (!isTradingWrappedToken ||
            CHAIN_CONFIG[chain as Chain]?.wrappedTokenAddress !==
              targetTokenConfig[selectedTargetChain]?.token) &&
          (!tradeModeConfig.from.isBlockedSameChain || chain !== selectedNativeChain),
      ).map((chain) => {
        return {
          chain,
          amount: getUserBalance(chain, "amount"),
          usdBalance: getUserBalance(chain, "usd"),
          imageUrl: CHAIN_CONFIG[chain as Chain].imageUrl,
          currency: CHAIN_CONFIG[chain as Chain].currency,
        };
      });
    } else if (isTradingWrappedToken) {
      return [CHAIN_CONFIG[selectedTargetChain]].map((config) => ({
        amount: getUserBalance(config.name, "amount"),
        usdBalance: getUserBalance(config.name, "usd"),
        imageUrl: config.imageUrl,
        currency: config.currency,
        chain: config.name,
      }));
    } else if (targetTokenConfig[selectedTargetChain]) {
      return [targetTokenConfig[selectedTargetChain]].map((config) => ({
        chain: selectedTargetChain,
        amount: targetBalanceAmount,
        usdBalance: targetBalanceUsd,
        imageUrl: config.imageUrl,
        currency: config.symbol,
      }));
    }
  }, [
    tradeModeConfig.from.isNative,
    tradeModeConfig.from.isBlockedSameChain,
    tradeModeConfig.supportedNativeChains,
    isTradingWrappedToken,
    targetTokenConfig,
    selectedTargetChain,
    selectedNativeChain,
    getUserBalance,
    targetBalanceAmount,
    targetBalanceUsd,
  ]);

  const toTokenList = useMemo(() => {
    if (tradeModeConfig.to.isNative) {
      return tradeModeConfig.supportedNativeChains[
        tradeModeConfig.to.isBlockedSameChain ? props.chain : selectedNativeChain
      ]!.filter((chain) => {
        return (
          (!tradeModeConfig.to.isBlockedSameChain || chain !== selectedNativeChain) &&
          (!isTradingWrappedToken ||
            CHAIN_CONFIG[chain as Chain]?.wrappedTokenAddress !==
              targetTokenConfig[selectedTargetChain]?.token)
        );
      }).map((chain) => {
        return {
          chain,
          amount: getUserBalance(chain, "amount"),
          symbol: CHAIN_CONFIG[chain as Chain].symbol,
          imageUrl: CHAIN_CONFIG[chain as Chain].imageUrl,
          currency: CHAIN_CONFIG[chain as Chain].currency,
        };
      });
    }
    return [targetTokenConfig[selectedTargetChain]].map((config) => ({
      chain: selectedTargetChain,
      amount: targetBalanceAmount,
      imageUrl: config.imageUrl,
      currency: config.symbol,
      selected: true,
    }));
  }, [
    tradeModeConfig.to.isNative,
    tradeModeConfig.to.isBlockedSameChain,
    tradeModeConfig.supportedNativeChains,
    targetTokenConfig,
    selectedTargetChain,
    props.chain,
    selectedNativeChain,
    isTradingWrappedToken,
    getUserBalance,
    targetBalanceAmount,
  ]);

  const setModeHandler = (mode: Mode) => {
    handleSetMode(mode);
    setFromToken(null);
    setToToken(null);
    isLoadingRef.current = false;
  };

  useEffect(() => {
    if (!isLoadingRef.current && !fromToken && !toToken && fromTokenList && toTokenList) {
      isLoadingRef.current = true;
      const fromTokenChain = props.chain ?? selectedNativeChain;

      setFromToken(isTradingWrappedToken ? fromTokenList[0]?.chain : fromTokenChain);
      setToToken(isTradingWrappedToken ? toTokenList[0]?.chain : selectedTargetChain);

      handleSetSelectedNativeChain(
        isTradingWrappedToken
          ? mode === "BUY"
            ? fromTokenList[0]?.chain
            : toTokenList[0]?.chain
          : fromTokenChain,
      );
    }
  }, [
    mode,
    toToken,
    fromToken,
    toTokenList,
    fromTokenList,
    handleSetSelectedNativeChain,
    isTradingWrappedToken,
    selectedNativeChain,
    selectedTargetChain,
    props.chain,
  ]);

  useEffect(() => {
    if ((!priceImpact && !noValidRouteMessage) || isLoadingPriceImpact || !!priceHint) {
      return setHintMessage("");
    }

    let hint = "Insufficient balance for trading";
    if (noValidRouteMessage) {
      hint = noValidRouteMessage;
      if (
        priceHint &&
        CHAIN_CONFIG[selectedTargetChain].isEvm &&
        !tradeModeConfig.from.isNative &&
        tradeModeConfig.to.isNative &&
        !isApproveSucess
      ) {
        hint = "Approval amount exceeded balance.";
      }

      setInputText("$0.00");
      setHintMessage(hint);
    } else if (isBalanceExceeded) {
      setInputText("$0.00");
      setHintMessage(hint);
    } else if (priceImpact && priceImpact / +inputUsd > 0.05) {
      setHintMessage("Price impact exceeded 5% of your trade size. Proceed with caution.");
    } else {
      setHintMessage("");
    }
  }, [
    inputUsd,
    isApproveSucess,
    isBalanceExceeded,
    isLoadingPriceImpact,
    noValidRouteMessage,
    priceHint,
    priceImpact,
    selectedTargetChain,
    setInputText,
    targetAmount,
    toToken,
    tradeModeConfig.from.isNative,
    tradeModeConfig.to.isNative,
  ]);

  const onClickNumber = (index: number) => {
    isCheckingAmountRef.current = true;
    onNumpadPress(`${index + 1}` as NumpadKey);
  };

  const onClickPercent = (index: number) => {
    isCheckingAmountRef.current = false;
    onPercentPress(index);
  };

  return (
    <>
      <DarkDrawer anchor="bottom" sx={{ zIndex: 100 }} open={props.open}>
        {/* Tool Bar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Slippage Button */}
          <Box>
            <CircleButton variant="white" size="lg" onClick={handleOpenSlippageDrawer}>
              <Tune />
            </CircleButton>
          </Box>
          {/* Buy / Sell */}
          <BuySellSwitch mode={mode} handleSetMode={setModeHandler} />
          {/* Close Button */}
          <Box>
            <CircleButton
              variant="white"
              size="lg"
              onClick={() => {
                isCheckingAmountRef.current = false;
                onClose();
              }}>
              <Close />
            </CircleButton>
          </Box>
        </Box>
        {/* Trade detail */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "32px",
            gap: "8px",
          }}>
          {/* Source Token Select (balance USD) */}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "end", alignItems: "center" }}>
            <Box sx={{ width: "158px" }}>
              <StyledSelect
                value={fromToken}
                noPointer={!isSelectable || !tradeModeConfig.from.isNative}
                sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: {
                        borderRadius: theme.spacing(1.5),
                        backgroundColor: theme.palette.surface.silver,
                      },
                    },
                  },
                }}
                onChange={(event) => onChangeSelect("from", event.target.value as Chain)}
                IconComponent={
                  !isSelectable || !tradeModeConfig.from.isNative ? () => null : undefined
                }>
                {/* Default Option */}
                {fromTokenList?.map((token) => (
                  <MenuItem
                    value={token?.chain}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: theme.spacing(1),
                    }}>
                    <img
                      src={token?.imageUrl}
                      alt={token?.chain}
                      width={16}
                      height={16}
                      style={{ borderRadius: "50%" }}
                    />
                    <Typography
                      sx={{ width: "100%", textAlign: "center" }}
                      variant="body-default"
                      color={theme.palette.text.secondary}>
                      <Value
                        variant="body-default"
                        value={token?.usdBalance!}
                        useFixedDecimals
                        zeroCountSignificantDigits={2}
                        smallNumberSignificantDigits={2}
                      />{" "}
                      USD
                      {/* {token?.currency} */}
                    </Typography>
                  </MenuItem>
                ))}
              </StyledSelect>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <ArrowRight width={24} />
          </Box>
          <Box sx={{ flex: 1, display: "flex", justifyContent: "start", alignItems: "center" }}>
            <Box sx={{ width: "158px" }}>
              <StyledSelect
                value={
                  tradeModeConfig.to.isBlockedSameChain && toToken === selectedNativeChain
                    ? toTokenList[0]?.chain
                    : toToken
                }
                defaultValue={toTokenList[0]?.chain}
                noPointer={!isSelectable || !tradeModeConfig.to.isNative}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: theme.spacing(1),
                }}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: {
                        borderRadius: theme.spacing(1.5),
                        backgroundColor: theme.palette.surface.silver,
                      },
                    },
                  },
                }}
                onChange={(event) => onChangeSelect("to", event.target.value as Chain)}
                IconComponent={
                  !isSelectable || !tradeModeConfig.to.isNative ? () => null : undefined
                }>
                {/* Default Option */}
                {toTokenList.map((token) => (
                  <MenuItem
                    value={token.chain}
                    sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
                    <img src={token.imageUrl} alt={token.chain} width={16} height={16} />
                    <Typography
                      sx={{ width: "100%", textAlign: "center" }}
                      variant="body-default"
                      color={theme.palette.text.secondary}>
                      {token.currency}
                    </Typography>
                  </MenuItem>
                ))}
              </StyledSelect>
            </Box>
          </Box>
        </Box>

        {/* Price Display */}
        <Box sx={{ marginTop: theme.spacing(1), marginX: "auto", textAlign: "center" }}>
          {/* Entered Value (USD) */}
          <Box>
            <Value
              variant="display-md"
              value={inputUsd}
              isPrice
              useFixedDecimals
              useKMBTFormat={false}
              inputText={inputText}
            />
          </Box>
          {/* Target Token (Amount) Received Preview */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Typography
              variant="body-md"
              color={theme.palette.text.secondary}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
              ≈{" "}
              <Value
                variant="body-md"
                value={tradeModeConfig.to.isNative ? nativeAmount : targetAmount}
              />
              {tradeModeConfig.to.isNative ? (
                <img
                  src={CHAIN_CONFIG[selectedNativeChain as Chain].imageUrl}
                  alt={CHAIN_CONFIG[selectedNativeChain]?.symbol}
                  width={16}
                  height={16}
                  style={{ margin: "auto" }}
                />
              ) : (
                <img
                  src={targetTokenConfig[selectedTargetChain]?.imageUrl}
                  alt={targetTokenConfig[selectedTargetChain]?.symbol}
                  width={16}
                  height={16}
                  style={{ borderRadius: "50%" }}
                />
              )}{" "}
              {/* ({numberFormatter.format(price)} USD) */}
            </Typography>
          </Box>
        </Box>
        {/* Remindar */}
        {!!hintMessage && (
          <RefetchPreview
            isLoading={isLoadingPriceImpact}
            isRefetching={isRefetchingPriceImpact}
            refetch={refetchPriceImpact}
            message={hintMessage}
          />
        )}
        {!!priceHint && !hintMessage && priceHint}
        {/* Percentage */}
        <Box
          display={"flex"}
          gap={theme.spacing(1)}
          marginTop={theme.spacing(1)}
          width={"fit-content"}
          marginX={"auto"}>
          {buttonConfig.map((item) => (
            <RetroButton size="sm" key={item.label} onClick={() => onClickPercent(item.value)}>
              <Box sx={{ padding: theme.spacing(0.0, 2) }}>
                <Typography variant="body-default">{item.label}</Typography>
              </Box>
            </RetroButton>
          ))}
        </Box>

        {/* Numpad */}
        <Box
          marginTop={theme.spacing(1)}
          display={"flex"}
          flexWrap={"wrap"}
          width={"100%"}
          marginX={"auto"}
          maxWidth={"600px"}>
          {Array.from({ length: 9 }).map((_, index) => (
            <NumpadButton key={index} onClick={() => onClickNumber(index)}>
              <Typography variant="title-h1">{index + 1}</Typography>
            </NumpadButton>
          ))}
          <NumpadButton onClick={() => onNumpadPress(".")}>
            <Typography variant="title-h1">.</Typography>
          </NumpadButton>
          <NumpadButton onClick={() => onNumpadPress("0")}>
            <Typography variant="title-h1">0</Typography>
          </NumpadButton>
          <NumpadButton
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={() => onNumpadPress("delete")}>
            <XSquare />
          </NumpadButton>
        </Box>

        {/* Confirm Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            marginBottom: "4px",
          }}>
          <Typography variant="pre-title" color={theme.palette.text.secondary}>
            {priceImpact && (
              <>
                <SmallNumberView value={priceImpact} variants="pre-title" isUSD />
                {" price impact  • "}
              </>
            )}
            {bridgeFee > 0 && (
              <>
                {"$"}
                <Value variant="pre-title" value={bridgeFee} />
                {" bridge fee  • "}
              </>
            )}
            {slippage ? <Value variant="pre-title" value={slippage} /> : "--"}% slippage
          </Typography>
        </Box>

        <Box sx={{ maxHeight: "69px", width: "100%" }}>
          {isLoadingPriceImpact ||
          (CHAIN_CONFIG[selectedTargetChain].isEvm &&
            !tradeModeConfig.from.isNative &&
            tradeModeConfig.to.isNative &&
            !isApproveSucess) ? (
            <RetroButton
              onClick={handleApproveAllowance}
              disabled={
                shouldConfirmDisabled ||
                isApproving ||
                isGetCurrentAllowanceLoading ||
                isLoadingPriceImpact
              }>
              <Box sx={{ padding: theme.spacing(1.5, 3) }}>
                <Typography variant="title-h3">
                  {isGetCurrentAllowanceLoading || isLoadingPriceImpact
                    ? "Loading..."
                    : "APPROVE SPENDING"}
                </Typography>
              </Box>
            </RetroButton>
          ) : (
            <RetroButton
              variant={tradeModeConfig.ctaColor}
              onClick={() => handleConfirm(slippage)}
              disabled={shouldConfirmDisabled || !!noValidRouteMessage || !!isBalanceExceeded}>
              <Box sx={{ padding: theme.spacing(1.5, 3) }}>
                <Typography variant="title-h3">CONFIRM {mode}</Typography>
              </Box>
            </RetroButton>
          )}
        </Box>
      </DarkDrawer>

      {/* Amount too small drawer */}
      <AmountTooSmallDrawer
        isOpen={shouldShowAmountTooSmall}
        close={() => setShouldShowAmountTooSmall(false)}
      />

      {/* Confirm drawer */}
      <ConfirmDrawer
        mode={mode}
        isOpen={isOpenConfirmDrawer}
        close={() => handleOpenConfirmDrawer(false)}
        inputUsd={inputUsd}
        targetAmount={targetAmount}
        targetTokenPrice={targetTokenPrice ?? 0}
        targetTokenSymbol={targetTokenConfig[selectedTargetChain]?.symbol ?? ""}
        onConfirm={(slippage) => {
          onConfirm(slippage);
          handleOpenConfirmDrawer(false);
        }}
      />

      {/* Slippage Drawer */}
      <PreferencesDrawer
        isOpen={isOpenSlippageDrawer}
        close={() => setIsOpenSlippageDrawer(false)}
        showPriorityFee={
          CHAIN_CONFIG[getChainsByMode(mode, selectedNativeChain, selectedTargetChain).from]
            .hasPriorityFee
        }
        showValidatorTip={
          CHAIN_CONFIG[getChainsByMode(mode, selectedNativeChain, selectedTargetChain).from]
            .hasValidatorTip
        }
      />

      {/* Modal */}
      <RetroModal
        open={modalConfig !== null}
        onClose={() => {
          setModalConfig(null);
        }}
        variant={modalConfig?.variant ?? "success"}
        title={modalConfig?.title ?? ""}
        message={modalConfig?.message ?? ""}
      />
    </>
  );
};

const NumpadButton = styled("button")(({ theme }) => ({
  all: "unset",
  width: "33%",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  textAlign: "center",

  background: "transparent",
  border: "none",
  cursor: "pointer",
}));
